import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router-dom";
import CorporateUserDetailsComponent from "./component";

const CorporateUserDetailsContainer = (props) => {
  const { corporateId } = useParams();
  const navigate = useNavigate();

  const [BPList, setBPList] = useState([]);
  const [filteredBPList, setFilteredBPList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editedDepartmentId, setEditedDepartmentId] = useState("");
  const [editedDepartmentName, setEditedDepartmentName] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("active");
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [corporateData, setCorporateData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Refresh BP List function
  const refreshBPList = () => {
    handleGetCorporateDetails();
  };

  useEffect(() => {
    if (corporateId) {
      handleGetCorporateDetails();
    } else {
      toastr.error("Error", "Invalid corporate ID");
      setInitialLoading(false);
    }
  }, [corporateId]);

  useEffect(() => {
    filterBPList();
  }, [BPList, searchValue, currentStatusFilter]);

  const handleGetCorporateDetails = (offsetValue = 0) => {
    setLoading(true);
    setInitialLoading(true);

    const apiUrl = `/partner/api/v1/business-profile/${corporateId}?limit=50&offset=${offsetValue}`;

    props.getDataFromAPI(
      apiUrl,
      "GET",
      undefined,
      (response) => {
        if (response && response) {
          setCorporateData(response);
          
          if (response.bpUserInfoList && Array.isArray(response.bpUserInfoList)) {
            const users = response.bpUserInfoList;
            setBPList(users);
            setFilteredBPList(users);
            
            // Extract unique departments - FILTER OUT DEFAULT DEPARTMENT
            const deptMap = new Map();
            users.forEach(user => {
              // Only add department if it has valid id and name and NOT default
              if (user.departmentId && user.departmentName && 
                  user.departmentName.toLowerCase() !== 'default' &&
                  user.departmentId !== 0) {
                if (!deptMap.has(user.departmentId)) {
                  deptMap.set(user.departmentId, {
                    id: user.departmentId,
                    name: user.departmentName
                  });
                }
              }
            });
            const deptList = Array.from(deptMap.values());
            setDepartments(deptList);
          } else {
            setBPList([]);
            setFilteredBPList([]);
            setDepartments([]);
          }
        } else if (response && response.bpUserInfoList) {
          setCorporateData(response);
          setBPList(response.bpUserInfoList);
          setFilteredBPList(response.bpUserInfoList);
          
          const deptMap = new Map();
          response.bpUserInfoList.forEach(user => {
            if (user.departmentId && user.departmentName && 
                user.departmentName.toLowerCase() !== 'default' &&
                user.departmentId !== 0) {
              if (!deptMap.has(user.departmentId)) {
                deptMap.set(user.departmentId, {
                  id: user.departmentId,
                  name: user.departmentName
                });
              }
            }
          });
          const deptList = Array.from(deptMap.values());
          setDepartments(deptList);
        } else {
          setCorporateData(null);
          setBPList([]);
          setFilteredBPList([]);
          setDepartments([]);
        }
        
        setLoading(false);
        setInitialLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error("Failed", "Unable to fetch corporate details.");
        setLoading(false);
        setInitialLoading(false);
      }
    );
  };

  const filterBPList = () => {
    let filtered = [...BPList];
    
    if (currentStatusFilter === "active") {
      filtered = filtered.filter((user) => user.status === true);
    } else if (currentStatusFilter === "inactive") {
      filtered = filtered.filter((user) => user.status === false);
    }
    
    if (searchValue && searchValue.trim() !== "") {
      const searchLower = searchValue.toLowerCase().trim();
      filtered = filtered.filter(
        (user) =>
          (user.usersCorporateId?.toString().toLowerCase().includes(searchLower)) ||
          (user.userName?.toLowerCase().includes(searchLower)) ||
          (user.emailId?.toLowerCase().includes(searchLower))
      );
    }
    
    setFilteredBPList(filtered);
    setSelectedRows([]);
  };

  const getSelectedEmployeeIds = () => {
    return selectedRows
      .map((index) => filteredBPList[index]?.usersCorporateId)
      .filter(Boolean);
  };

  const getSelectedUcmIds = () => {
    return selectedRows
      .map((index) => filteredBPList[index]?.ucmId)
      .filter(Boolean);
  };

  const getDepartmentIdForSelectedUsers = () => {
    if (selectedRows.length === 0) return null;
    
    const selectedUsers = selectedRows.map(index => filteredBPList[index]);
    const firstDeptId = selectedUsers[0]?.departmentId;
    const allSameDepartment = selectedUsers.every(user => user?.departmentId === firstDeptId);
    
    return allSameDepartment ? firstDeptId : null;
  };

  // Single User Activate
  const handleActivateClick = (index) => {
    setCurrentRowIndex(index);
    setActivateOpen(true);
  };

  const handleConfirmActivate = () => {
    if (currentRowIndex === null) return;
    
    const userToActivate = filteredBPList[currentRowIndex];
    const employeeId = userToActivate?.usersCorporateId;
    
    if (!employeeId) {
      toastr.error("Error", "No employee ID found to activate.");
      setActivateOpen(false);
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}/activate`,
      "POST",
      { employeeIds: [employeeId] },
      () => {
        toastr.success("Success", "User activated successfully.");
        setActivateOpen(false);
        refreshBPList();
        setSelectedRows([]);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to activate user.";
        toastr.error("Failed", errorMsg);
        setActivateOpen(false);
        setLoading(false);
      }
    );
  };

  // Single User Deactivate
  const handleDeactivateClick = (index) => {
    setCurrentRowIndex(index);
    setDeactivateOpen(true);
  };

  const handleConfirmDeactivate = () => {
    if (currentRowIndex === null) return;
    
    const userToDeactivate = filteredBPList[currentRowIndex];
    const employeeId = userToDeactivate?.usersCorporateId;
    
    if (!employeeId) {
      toastr.error("Error", "No employee ID found to deactivate.");
      setDeactivateOpen(false);
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "DELETE",
      { employeeIds: [employeeId] },
      () => {
        toastr.success("Success", "User deactivated successfully.");
        setDeactivateOpen(false);
        refreshBPList();
        setSelectedRows([]);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to deactivate user.";
        toastr.error("Failed", errorMsg);
        setDeactivateOpen(false);
        setLoading(false);
      }
    );
  };

  // Bulk Activate
  const handleBulkActivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No users selected for activation.");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}/activate`,
      "POST",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", `${selectedEmployeeIds.length} user(s) activated successfully.`);
        setSelectedRows([]);
        refreshBPList();
      },
      (err) => {
        const errorMsg = err?.message || "Unable to activate users.";
        toastr.error("Failed", errorMsg);
        setLoading(false);
      }
    );
  };

  // Bulk Deactivate
  const handleBulkDeactivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No users selected for deactivation.");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "DELETE",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", `${selectedEmployeeIds.length} user(s) deactivated successfully.`);
        setSelectedRows([]);
        refreshBPList();
      },
      (err) => {
        const errorMsg = err?.message || "Unable to deactivate users.";
        toastr.error("Failed", errorMsg);
        setLoading(false);
      }
    );
  };

  // Bulk Delete (Permanent)
  const handleBulkDelete = () => {
    const selectedUcmIds = getSelectedUcmIds();
    
    if (selectedUcmIds.length === 0) {
      toastr.error("Error", "No users selected for deletion.");
      return;
    }
    
    const departmentId = getDepartmentIdForSelectedUsers();
    
    const payload = {
      employeeIds: selectedUcmIds,
      departmentId: departmentId
    };
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "DELETE",
      payload,
      () => {
        toastr.success("Success", `${selectedUcmIds.length} user(s) deleted successfully.`);
        setSelectedRows([]);
        refreshBPList();
      },
      (err) => {
        const errorMsg = err?.message || "Unable to delete users.";
        toastr.error("Failed", errorMsg);
        setLoading(false);
      }
    );
  };

  const handleDeleteClick = (index) => {
    setCurrentRowIndex(index);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (currentRowIndex === null) return;
    
    const userToDelete = filteredBPList[currentRowIndex];
    const ucmId = userToDelete?.ucmId;
    const departmentId = userToDelete?.departmentId;
    
    if (!ucmId) {
      toastr.error("Error", "No user ID found to delete.");
      setDeleteOpen(false);
      return;
    }
    
    const payload = {
      employeeIds: [ucmId],
      departmentId: departmentId
    };
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${corporateId}`,
      "DELETE",
      payload,
      () => {
        toastr.success("Success", "User deleted successfully.");
        setDeleteOpen(false);
        refreshBPList();
        setSelectedRows([]);
      },
      (err) => {
        const errorMsg = err?.message || "Unable to delete user.";
        toastr.error("Failed", errorMsg);
        setDeleteOpen(false);
        setLoading(false);
      }
    );
  };

  // Edit User
  const handleUpdateUser = (ucmId) => {
    if (!ucmId) {
      toastr.error("Error", "No user ID provided");
      return;
    }
    
    if (!editedDepartmentId) {
      toastr.error("Error", "Please select a department");
      return;
    }
    
    setLoading(true);
    
    const selectedDept = departments.find(d => d.id == editedDepartmentId);
    const deptName = selectedDept?.name || "";
    
    const payload = {
      userName: editedName,
      departmentId: parseInt(editedDepartmentId),
      departmentName: deptName
    };
    
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${ucmId}`,
      "PATCH",
      payload,
      () => {
        toastr.success("Success", "User updated successfully");
        setEditOpen(false);
        refreshBPList();
        setSelectedRows([]);
        setEditedDepartmentId("");
        setEditedDepartmentName("");
      },
      (err) => {
        const errorMsg = err.message || "Something went wrong.";
        toastr.error("Error", errorMsg);
        setLoading(false);
      }
    );
  };

  // Unlink User
  const handleConfirmUnlink = () => {
    const userToUnlink = filteredBPList[currentRowIndex];
    const ucmId = userToUnlink?.ucmId;
    if (!ucmId) {
      toastr.error("Error", "No employee ID found to unlink.");
      return;
    }
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/approve/${ucmId}`,
      "POST",
      undefined,
      () => {
        toastr.success("Success", "User unlinked successfully.");
        setUnlinkOpen(false);
        refreshBPList();
        setSelectedRows([]);
      },
      (err) => {
        const errorMsg = err?.status?.message || "Something went wrong.";
        toastr.error("Error", errorMsg);
        setLoading(false);
      }
    );
  };

  const handleSearchChange = (e) => setSearchValue(e.target.value);
  
  const handleStatusToggle = (status) => {
    setCurrentStatusFilter(status);
  };

  const handleRowSelection = (index) => {
    setSelectedRows(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredBPList.map((_, i) => i));
    } else {
      setSelectedRows([]);
    }
  };

  const handleEditClick = (index) => {
    setCurrentRowIndex(index);
    const user = filteredBPList[index];
    setEditedName(user.userName || '');
    setEditedDepartmentId(user.departmentId || '');
    setEditedDepartmentName(user.departmentName || '');
    setEditOpen(true);
  };

  const handleSaveEdit = () => {
    if (currentRowIndex === null) return;
    const userToUpdate = filteredBPList[currentRowIndex];
    handleUpdateUser(userToUpdate.ucmId);
  };

  const handleUnlinkClick = (index) => {
    setCurrentRowIndex(index);
    setUnlinkOpen(true);
  };

  const handleAddNewCorporateUser = () => {
    navigate(`/home/customermanager/bpmanagementlist/corporatemanagementusercreation/${corporateId}`);
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom && !loading) {
      // Implement pagination if needed
    }
  };

  return (
    <CorporateUserDetailsComponent
      corporateData={corporateData}
      BPList={filteredBPList}
      allUsers={BPList}
      selectedRows={selectedRows}
      handleRowSelection={handleRowSelection}
      handleSelectAll={handleSelectAll}
      editOpen={editOpen}
      setEditOpen={setEditOpen}
      editedName={editedName}
      setEditedName={setEditedName}
      editedDepartmentId={editedDepartmentId}
      setEditedDepartmentId={setEditedDepartmentId}
      editedDepartmentName={editedDepartmentName}
      setEditedDepartmentName={setEditedDepartmentName}
      handleEditClick={handleEditClick}
      handleSaveEdit={handleSaveEdit}
      unlinkOpen={unlinkOpen}
      setUnlinkOpen={setUnlinkOpen}
      handleUnlinkClick={handleUnlinkClick}
      handleConfirmUnlink={handleConfirmUnlink}
      searchValue={searchValue}
      handleSearchChange={handleSearchChange}
      handleStatusToggle={handleStatusToggle}
      currentStatusFilter={currentStatusFilter}
      handleAddNewCorporateUser={handleAddNewCorporateUser}
      handleScroll={handleScroll}
      loading={loading || initialLoading}
      departments={departments}
      deleteOpen={deleteOpen}
      setDeleteOpen={setDeleteOpen}
      handleDeleteClick={handleDeleteClick}
      handleConfirmDelete={handleConfirmDelete}
      handleBulkDelete={handleBulkDelete}
      activateOpen={activateOpen}
      setActivateOpen={setActivateOpen}
      handleActivateClick={handleActivateClick}
      handleConfirmActivate={handleConfirmActivate}
      handleBulkActivate={handleBulkActivate}
      deactivateOpen={deactivateOpen}
      setDeactivateOpen={setDeactivateOpen}
      handleDeactivateClick={handleDeactivateClick}
      handleConfirmDeactivate={handleConfirmDeactivate}
      handleBulkDeactivate={handleBulkDeactivate}
    />
  );
};

export default connect(null, { getDataFromAPI })(CorporateUserDetailsContainer);